import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ViewContentParamsInterface } from '../models/view-content-params.interface';

declare let fbq: any;

@Injectable({
  providedIn: 'root'
})
export class PixelService {

  // Must match the pixel ID initialised by the index html snippet (fbq('init', ...))
  private pixelId = '3742034582748874';

  constructor(@Inject(PLATFORM_ID) private platformId: Object) { }

  /**
   * This method is used to generate a unique event ID shared between the browser pixel
   * and the Conversions API so Meta can deduplicate events sent from both sides
   * @returns Unique event ID
   */
  generateEventId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return 'evt-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  /**
   * This method is used to check the base pixel availability.
   * The base pixel (fbq init + PageView) is loaded via the index html snippet,
   * so this returns false on the server and on environments where the snippet is disabled
   */
  private isPixelAvailable(): boolean {
    // Skip during server-side rendering
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }
    return typeof fbq === 'function';
  }

  /**
   * This method is used to fire a Meta Pixel event against the exact pixel ID.
   * trackSingle/trackSingleCustom pin the event to this.pixelId, so it never
   * double-fires if another pixel is ever initialised on the page (e.g. via GTM)
   * @param eventType
   * @param eventName
   * @param params
   * @param options
   */
  private fire(eventType: 'track' | 'trackCustom', eventName: string, params?: object, options?: { eventID?: string }): void {
    if (!this.isPixelAvailable()) {
      return;
    }
    const singleEventType = eventType === 'track' ? 'trackSingle' : 'trackSingleCustom';
    if (options?.eventID) {
      fbq(singleEventType, this.pixelId, eventName, params ?? {}, { eventID: options.eventID });
    } else {
      fbq(singleEventType, this.pixelId, eventName, params ?? {});
    }
  }

  /**
   * This method is used to track the PageView event
   * @param eventId
   */
  pageView(eventId?: string): void {
    this.fire('track', 'PageView', {}, { eventID: eventId });
  }

  /**
   * This method is used to track the ViewContent event
   * @param params
   * @param eventId
   */
  viewContent(params: ViewContentParamsInterface, eventId?: string): void {
    this.fire('track', 'ViewContent', params, { eventID: eventId });
  }

  /**
   * This method is used to track a standard Meta Pixel event
   * @param eventName
   * @param params
   * @param eventId
   */
  track(eventName: string, params: object = {}, eventId?: string): void {
    this.fire('track', eventName, params, { eventID: eventId });
  }

  /**
   * This method is used to track a custom (non-standard) Meta Pixel event
   * @param eventName
   * @param params
   * @param eventId
   */
  trackCustom(eventName: string, params: object = {}, eventId?: string): void {
    this.fire('trackCustom', eventName, params, { eventID: eventId });
  }
}
